/**
 * useSurfacePolicy — Patch 37.4.12 (Public Demo Cost & Provider Scrubbing).
 *
 * The single canonical helper for "can the current UI show internal
 * diagnostics right now?" — provider names, model names, per-request
 * costs, quality scores, token/unit counts, router decisions, retry
 * counts, fallback providers, cost-ledger data, production specs,
 * internal Founder/Admin tabs.
 *
 * ─── Rule ────────────────────────────────────────────────────────
 *
 *   canViewInternalDiagnostics = (
 *       user is an authorized founder/admin
 *     AND
 *       the active workspace is NOT a public/competition demo
 *     AND
 *       Founder Mode preference is enabled (default-on per Patch 37.4.7)
 *   )
 *
 * Public demo context is detected from ``workspace.settings.is_demo_workspace``
 * — a server-authoritative flag set at workspace-creation time in
 * ``services/demo_service.py`` and force-refreshed on every
 * ``/demo/login``. A query parameter, sessionStorage value, seeded
 * owner role, or demo authentication state CANNOT override it.
 *
 * This is the trust boundary that keeps the demo positioned as a
 * finished-outcome product ("Image generation available · Ready ·
 * Complete") rather than a supplier-invoice preview ("$0.0400 · q 92
 * · openai.gpt_image_1").
 *
 * Every diagnostic-emitting UI surface MUST consult this hook.
 * Route-specific role checks and duplicated ``isAdmin`` conditions
 * are forbidden — they always drift.
 */
import { useAuthStore } from "@/entities/auth/store";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useFounderMode } from "@/entities/auth/useFounderMode";

export interface SurfacePolicy {
  /** True only for real production workspaces + authorized founders. */
  canViewInternalDiagnostics: boolean;
  /** True whenever the active workspace is a public/competition demo. */
  isPublicDemoContext: boolean;
  /** True when the caller is server-authorized (owner/admin/is_dev_user). */
  isAuthorized: boolean;
}

export function useSurfacePolicy(): SurfacePolicy {
  const user = useAuthStore((s) => s.user);
  const workspace = useCurrentWorkspace();
  const founderModeEnabled = useFounderMode();

  const isAuthorized = user?.is_dev_user === true;

  // Server-authoritative demo detection. The flag is stamped on the
  // workspace document by ``demo_service`` on creation and on every
  // ``/demo/login`` — no client-side path can toggle it.
  const isPublicDemoContext = Boolean(
    (workspace?.settings as unknown as { is_demo_workspace?: boolean } | undefined)
      ?.is_demo_workspace,
  );

  const canViewInternalDiagnostics =
    isAuthorized && !isPublicDemoContext && founderModeEnabled;

  return { canViewInternalDiagnostics, isPublicDemoContext, isAuthorized };
}
